import { AnimatePresence, type MotionProps, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import { useTheme } from 'styled-components';

import { CheckmarkIcon, CloseIcon } from '@treams/treams-icons';
import {
	MfaMethod,
	type UserMfaConfigurationFragment,
	WorkspaceMfaSettingsFragment,
	WorkspaceMfaStatus,
	useAssociateTotpTokenMutation,
	useDisableMfaMethodMutation,
} from '@treams/treams-network';
import { Button, Card, FlexGap, IconColor, Paragraph, Tooltip } from '@treams/treams-ui';

import SecretKeyVerification from './SecretKeyVerification';

const motionConfig: MotionProps = {
	style: { transformOrigin: 'top' },
	initial: { opacity: 0, scaleY: 0.95 },
	animate: { opacity: 1, scaleY: 1 },
	exit: { opacity: 0, scaleY: 1 },
	transition: { type: 'spring', stiffness: 500, damping: 40, duration: 4 },
};

export default function TotpConfiguration({
	userMfaConfiguration: configuration,
	workspaceMfaSettings,
}: {
	userMfaConfiguration: UserMfaConfigurationFragment['userMfaConfiguration'];
	workspaceMfaSettings: WorkspaceMfaSettingsFragment;
}) {
	const { t } = useTranslation();
	const theme = useTheme();
	const [associateTotpToken, { data: associateTotpTokenData, reset }] =
		useAssociateTotpTokenMutation();
	const [disableTotpToken] = useDisableMfaMethodMutation({
		variables: { input: { method: MfaMethod.Totp } },
	});
	const secretData = associateTotpTokenData?.associateTotpToken;

	const isConfigured =
		configuration.enabled && configuration.enabledMethods?.includes(MfaMethod.Totp);
	const isMfaRequired = workspaceMfaSettings.status === WorkspaceMfaStatus.Required;

	return (
		<AnimatePresence initial={false} mode="wait">
			{secretData ? (
				<motion.div key={secretData.secretCode} {...motionConfig}>
					<Card p={4}>
						<SecretKeyVerification
							secretKey={secretData.secretCode}
							onDismiss={reset}
							onComplete={reset}
						/>
					</Card>
				</motion.div>
			) : (
				<motion.div {...motionConfig}>
					<FlexGap gap={8}>
						<Box size={24}>
							<IconColor color={isConfigured ? theme.colors.green : theme.colors.red}>
								{isConfigured ? <CheckmarkIcon /> : <CloseIcon />}
							</IconColor>
						</Box>
						<FlexGap gap={4} flexDirection="column" alignItems="flex-start">
							<Paragraph fontWeight="bold" color="antracite">
								{isConfigured
									? t('personalSettings.mfa.totp.enabled')
									: t('personalSettings.mfa.totp.disabled')}
							</Paragraph>
							<Paragraph>{t('personalSettings.mfa.totp.description')}</Paragraph>
							<FlexGap gap={8} mt={3}>
								<Button
									small
									type="transparent"
									title={
										isConfigured
											? t('personalSettings.mfa.totp.restartSetup')
											: t('personalSettings.mfa.totp.startSetup')
									}
									onClick={() => associateTotpToken({ variables: { input: {} } })}
								/>
								{isConfigured && (
									<Tooltip
										enabled={isMfaRequired}
										content={t('personalSettings.mfa.totp.mfaRequiredCannotDisable')}
										position="bottom"
									>
										<Button
											small
											type="secondary"
											title={t('common.disable')}
											onClick={() => disableTotpToken()}
											disabled={isMfaRequired}
										/>
									</Tooltip>
								)}
							</FlexGap>
						</FlexGap>
					</FlexGap>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
